// components/tutorial/tutorial-series-view.tsx
// Series view: each series is a row with horizontal tutorial slideshow
"use client";

import { memo, useCallback, useRef, useState } from "react";

import { cn } from "@vllnt/ui";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";

import { Link } from "@/i18n/routing";
import type { TutorialSeries, TutorialSeriesItem } from "@/types/tutorial";

export type TutorialSeriesViewProps = {
  searchQuery?: string;
  series: TutorialSeries[];
  tagFilters?: string[];
};

const EMPTY_TAG_FILTERS: string[] = [];

// Format series ID for display (e.g., "claude-code-mastery" -> "Claude Code Mastery")
function formatSeriesTitle(id: string): string {
  return id
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Filter series based on search and tags
function filterSeries(
  series: TutorialSeries[],
  searchQuery: string,
  tagFilters: string[],
): TutorialSeries[] {
  if (!searchQuery && tagFilters.length === 0) {
    return series;
  }

  return series.flatMap((s) => {
    const filteredTutorials = s.tutorials.filter((tutorial) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (
          !tutorial.title.toLowerCase().includes(query) &&
          !tutorial.description.toLowerCase().includes(query)
        ) {
          return false;
        }
      }
      return true;
    });

    return filteredTutorials.length > 0
      ? [{ ...s, tutorials: filteredTutorials }]
      : [];
  });
}

function TutorialSeriesViewImpl({
  searchQuery = "",
  series,
  tagFilters = EMPTY_TAG_FILTERS,
}: TutorialSeriesViewProps): React.ReactNode {
  const t = useTranslations("tutorials");

  const filteredSeries = filterSeries(series, searchQuery, tagFilters);

  if (filteredSeries.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">
          {searchQuery || tagFilters.length > 0
            ? t("no_tutorials_match")
            : t("no_series")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {filteredSeries.map((s) => (
        <SeriesRow key={s.id} series={s} />
      ))}
    </div>
  );
}

type ScrollControlsProps = {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  onScrollLeft: () => void;
  onScrollRight: () => void;
};

function ScrollControls({
  canScrollLeft,
  canScrollRight,
  onScrollLeft,
  onScrollRight,
}: ScrollControlsProps): React.ReactNode {
  const t = useTranslations("tutorials");

  if (!canScrollLeft && !canScrollRight) return null;

  return (
    <div className="flex gap-1">
      <button
        aria-label={t("scroll_left")}
        className={cn(
          "p-2 rounded-md border border-border transition-colors",
          canScrollLeft
            ? "hover:bg-muted cursor-pointer"
            : "opacity-40 cursor-not-allowed",
        )}
        disabled={!canScrollLeft}
        onClick={onScrollLeft}
        type="button"
      >
        <ChevronLeft className="size-5" />
      </button>
      <button
        aria-label={t("scroll_right")}
        className={cn(
          "p-2 rounded-md border border-border transition-colors",
          canScrollRight
            ? "hover:bg-muted cursor-pointer"
            : "opacity-40 cursor-not-allowed",
        )}
        disabled={!canScrollRight}
        onClick={onScrollRight}
        type="button"
      >
        <ChevronRight className="size-5" />
      </button>
    </div>
  );
}

type SeriesRowProps = {
  series: TutorialSeries;
};

function SeriesRowImpl({ series }: SeriesRowProps): React.ReactNode {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 10,
    );
  }, []);

  const handleContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      scrollContainerRef.current = node;
      setTimeout(() => {
        setCanScrollRight(node.scrollWidth > node.clientWidth);
      }, 0);
    }
  }, []);

  const scroll = useCallback((direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 400;
    container.scrollBy({
      behavior: "smooth",
      left: direction === "left" ? -scrollAmount : scrollAmount,
    });
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center size-8 rounded-full bg-foreground text-background text-sm font-bold">
            {series.tutorials.length}
          </div>
          <h3 className="text-xl font-semibold">
            {formatSeriesTitle(series.id)}
          </h3>
        </div>
        <ScrollControls
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
          onScrollLeft={() => {
            scroll("left");
          }}
          onScrollRight={() => {
            scroll("right");
          }}
        />
      </div>

      <div
        className="overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden -mx-4"
        onScroll={updateScrollButtons}
        ref={handleContainerRef}
      >
        <div className="flex gap-4 px-4">
          {series.tutorials.map((tutorial, index) => (
            <TutorialSlide
              index={index}
              key={tutorial.contentId}
              totalCount={series.tutorials.length}
              tutorial={tutorial}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

type TutorialSlideProps = {
  index: number;
  totalCount: number;
  tutorial: TutorialSeriesItem;
};

function TutorialSlideImpl({
  index,
  totalCount,
  tutorial,
}: TutorialSlideProps): React.ReactNode {
  const t = useTranslations("tutorials");

  return (
    <Link
      className="flex-shrink-0 w-[340px] sm:w-[400px] snap-start group"
      href={{ params: { slug: tutorial.slug }, pathname: "/tutorials/[slug]" }}
    >
      <div className="border border-border rounded-xl p-5 h-full bg-card hover:border-foreground/40 hover:bg-muted/30 transition-all">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center justify-center size-6 rounded-full bg-muted text-xs font-bold">
            {index + 1}
          </span>
          <span className="text-xs text-muted-foreground">
            {t("part_of_series", { order: index + 1, total: totalCount })}
          </span>
        </div>

        <h4 className="font-semibold text-base mb-2 group-hover:underline line-clamp-2">
          {tutorial.title}
        </h4>

        <p className="text-sm text-muted-foreground line-clamp-3">
          {tutorial.description}
        </p>
      </div>
    </Link>
  );
}

const TutorialSlide = memo(TutorialSlideImpl);
TutorialSlide.displayName = "TutorialSlide";

const SeriesRow = memo(SeriesRowImpl);
SeriesRow.displayName = "SeriesRow";

export const TutorialSeriesView = memo(TutorialSeriesViewImpl);
TutorialSeriesView.displayName = "TutorialSeriesView";
